import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// ai_copilot_controller
//
// Provides a collapsible AI Copilot chat panel on Asset/Task show pages.
// Sends questions to /companies/:id/ai_conversations and renders answers
// with optional citation chips.
//
// Enterprise: RAG-powered answers with citations.
// Growth+: Direct GPT answers without RAG.
//
// Write-back actions (task context only):
//   "Insert into notes" — appends the AI answer to task completion_notes
//   "Create checklist"  — parses the answer into new checklist items
//
// Usage:
//   <div data-controller="ai-copilot"
//        data-ai-copilot-url-value="..."
//        data-ai-copilot-asset-id-value="123"
//        data-ai-copilot-task-id-value="456"
//        data-ai-copilot-base-url-value="/companies/1/ai_conversations">
//     <button data-action="ai-copilot#toggle" data-ai-copilot-target="toggleBtn">
//       Ask AI
//     </button>
//     <div data-ai-copilot-target="panel" class="hidden">
//       <div data-ai-copilot-target="messages"></div>
//       <textarea data-ai-copilot-target="input"></textarea>
//       <button data-action="ai-copilot#send">Send</button>
//       <div data-ai-copilot-target="status"></div>
//     </div>
//   </div>

export default class extends Controller {
  static targets = ["panel", "toggleBtn", "messages", "input", "status", "sendBtn"]
  static values = { url: String, assetId: String, taskId: String, baseUrl: String }

  connect() {
    this.loading = false
    this.conversationId = null
  }

  toggle() {
    const isHidden = this.panelTarget.classList.contains("hidden")
    this.panelTarget.classList.toggle("hidden", !isHidden)
    if (this.hasToggleBtnTarget) {
      const icon = this.toggleBtnTarget.querySelector(".material-symbols-outlined")
      if (icon) icon.textContent = isHidden ? "close" : "smart_toy"
      const label = this.toggleBtnTarget.querySelector(".toggle-label")
      if (label) label.textContent = isHidden ? t("javascript.controllers_v2.ai_copilot_controller.close") : t("javascript.controllers_v2.ai_copilot_controller.ask_ai")
    }
    if (isHidden) {
      // Focus the input when opening
      setTimeout(() => this.inputTarget?.focus(), 100)
    }
  }

  async send() {
    if (this.loading) return
    const question = this.inputTarget?.value?.trim()
    if (!question) return

    this.inputTarget.value = ""
    this.#appendMessage("user", question)
    this.#setLoading(true)

    try {
      const body = { question }
      if (this.assetIdValue) body.asset_id = this.assetIdValue
      if (this.taskIdValue) body.task_id = this.taskIdValue
      if (this.conversationId) body.conversation_id = this.conversationId

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        this.#appendMessage("error", data.error || t("javascript.controllers_v2.ai_copilot_controller.something_went_wrong"))
        return
      }

      this.conversationId = data.conversation_id
      this.#appendMessage("assistant", data.answer, data.citations || [])
    } catch (e) {
      this.#appendMessage("error", t("javascript.controllers_v2.ai_copilot_controller.network_error"))
    } finally {
      this.#setLoading(false)
    }
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send()
    }
  }

  // Write-back: Insert AI answer into task notes
  async insertNote(event) {
    const btn = event.currentTarget
    const content = btn.dataset.content
    if (!content || !this.conversationId) return

    await this.#writeBack(btn, `${this.#baseConversationUrl()}/insert_note`, { content })
  }

  // Write-back: Convert AI answer into checklist items
  async createChecklist(event) {
    const btn = event.currentTarget
    const content = btn.dataset.content
    if (!content || !this.conversationId) return

    await this.#writeBack(btn, `${this.#baseConversationUrl()}/create_checklist`, { content })
  }

  // -- private helpers -------------------------------------------------------

  #baseConversationUrl() {
    const base = this.hasBaseUrlValue ? this.baseUrlValue : this.urlValue
    return `${base}/${this.conversationId}`
  }

  async #writeBack(btn, url, body) {
    btn.disabled = true
    const origText = btn.textContent
    btn.textContent = t("javascript.controllers_v2.ai_copilot_controller.saving")
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      const data = await response.json()
      if (response.ok) {
        btn.textContent = t("javascript.controllers_v2.ai_copilot_controller.done_with_message", { message: data.message || t("javascript.controllers_v2.ai_copilot_controller.done") })
        btn.classList.add("opacity-60")
      } else {
        btn.textContent = origText
        btn.disabled = false
        this.#appendMessage("error", data.error || t("javascript.controllers_v2.ai_copilot_controller.could_not_complete"))
      }
    } catch (e) {
      btn.textContent = origText
      btn.disabled = false
      this.#appendMessage("error", t("javascript.controllers_v2.ai_copilot_controller.network_error"))
    }
  }

  #appendMessage(role, content, citations = []) {
    const el = document.createElement("div")
    el.className = role === "user"
      ? "flex justify-end mb-3"
      : role === "error"
        ? "flex justify-start mb-3"
        : "flex justify-start mb-3"

    const bubble = document.createElement("div")
    bubble.className = role === "user"
      ? "max-w-[80%] px-3 py-2 rounded-xl rounded-tr-sm bg-primary text-white text-sm"
      : role === "error"
        ? "max-w-[80%] px-3 py-2 rounded-xl rounded-tl-sm bg-red-50 border border-red-200 text-red-700 text-sm"
        : "max-w-[80%] px-3 py-2 rounded-xl rounded-tl-sm bg-gray-100 text-gray-800 text-sm"

    bubble.textContent = content

    // Add citation chips for assistant messages
    if (role === "assistant" && citations.length > 0) {
      const citsDiv = document.createElement("div")
      citsDiv.className = "mt-2 flex flex-wrap gap-1"
      citations.forEach(cit => {
        const chip = document.createElement("span")
        chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium border border-violet-200"
        chip.innerHTML = `<span class="material-symbols-outlined !text-xs">inventory_2</span> ${this.#escape(cit.name || `${cit.type} #${cit.id}`)}`
        citsDiv.appendChild(chip)
      })
      bubble.appendChild(citsDiv)
    }

    // Write-back action buttons for assistant messages in task context
    if (role === "assistant" && this.taskIdValue) {
      const actionsDiv = document.createElement("div")
      actionsDiv.className = "mt-2 flex flex-wrap gap-1"

      const insertBtn = document.createElement("button")
      insertBtn.type = "button"
      insertBtn.dataset.content = content
      insertBtn.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-60"
      insertBtn.innerHTML = `<span class="material-symbols-outlined !text-xs">note_add</span> ${this.#escape(t("javascript.controllers_v2.ai_copilot_controller.insert_into_notes"))}`
      insertBtn.addEventListener("click", (e) => this.insertNote(e))

      const checklistBtn = document.createElement("button")
      checklistBtn.type = "button"
      checklistBtn.dataset.content = content
      checklistBtn.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-60"
      checklistBtn.innerHTML = `<span class="material-symbols-outlined !text-xs">checklist</span> ${this.#escape(t("javascript.controllers_v2.ai_copilot_controller.create_checklist"))}`
      checklistBtn.addEventListener("click", (e) => this.createChecklist(e))

      actionsDiv.appendChild(insertBtn)
      actionsDiv.appendChild(checklistBtn)
      bubble.appendChild(actionsDiv)
    }

    el.appendChild(bubble)
    this.messagesTarget.appendChild(el)
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  #setLoading(state) {
    this.loading = state
    if (this.hasSendBtnTarget) {
      this.sendBtnTarget.disabled = state
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = state ? t("javascript.controllers_v2.ai_copilot_controller.thinking") : ""
    }
    if (state) {
      // Show typing indicator
      const typing = document.createElement("div")
      typing.id = "ai-typing-indicator"
      typing.className = "flex justify-start mb-3"
      typing.innerHTML = `
        <div class="px-3 py-2 rounded-xl rounded-tl-sm bg-gray-100 text-gray-500 text-sm flex items-center gap-1">
          <span class="animate-bounce delay-0">•</span>
          <span class="animate-bounce delay-100">•</span>
          <span class="animate-bounce delay-200">•</span>
        </div>
      `
      this.messagesTarget.appendChild(typing)
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    } else {
      document.getElementById("ai-typing-indicator")?.remove()
    }
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  #escape(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
